import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { merge } from 'lodash';
import { ChevronDown } from 'lucide-react';
import { toJS } from 'mobx';
import queryString from 'query-string'
import { Row, Col, Button, Label, FormGroup, SimpleAlert, FormBody, Tabs, TabsList, TabsTrigger, TabsContent, InputGroup, DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DefaultTooltip, FormSection, FormFooter, FormCard } from 'twyne-new.ui';

import Formsy from '@Components/form-new/FormContainer'
import InputFormsy from '@Components/formsy/Input';
import RuleGroupItemWrapper from '@Components/pure/rules/RuleGroup/Item';
import { buttonLabels } from '@Components/rules/options';
import InfoTip from 'components/InfoTip';
import Input from 'components/form/Input';
import Select from 'components/form/Select';
import Switch from 'components/form/Switch';
import appStore from 'stores/app';
import history from 'utils/history';

import IntegrationRules from './Rules';
import SweepContestFinishedForm from './SweepContestFinishedForm/Layout';
import { defaultSCAConfiguration } from './SweepContestFinishedForm/options';
import customForms from './forms'
import { useTypes, useVendors } from './utils';

class IntegrationForm extends React.Component {
  state = {
    vendor: '',
    integration_type: '',
    configuration: null,
    isRegistration: false,
    active: true,
    rules: null,
    conflictEvent: null,
  }

  laodData = async data => {
    const { integration_type = '', vendor = null, configuration = {} } = data
    const isRegistration = integration_type.includes("register") ? true : false

    this.setState({
      ...data,
      vendor,
      integration_type,
      configuration,
      isRegistration,
    })
  }

  componentDidMount() {
    this.laodData(toJS(this.props.data) || {})
    this.handleSearchState()
  }

  handleConflict = (event) => {
    this.setState({ conflictEvent: event })
  }

  handleSearchState = () => {
    try {
      const query = queryString.parse(history.location.search)

      if (query && query.configure) {
        const state = JSON.parse(atob(query.configure)) || {}
        this.setState({ ...state })
      }

      history.replace({ search: '' })
    } catch (error) {
      console.error(error)
    }
  }

  handleValidSubmit = ({ configuration: modelConfiguration, ...model }) => {
    const { configuration: stateConfiguration, ...state } = this.state

    const neededConfigurationCall = ['delayed_register', 'register', 'path_impression', 'lead_status_sync'].includes(this.state.integration_type)
    const isHttp = ['http_get', 'http_post'].includes(this.state.vendor)
    const isRevenue = ['revenue_postback', 'revenue_poller', 'revenue_share'].includes(this.state.integration_type)

    const configuration = (neededConfigurationCall || isRevenue) && isHttp ? this.getConfiguration() : isRevenue ? modelConfiguration : merge({}, stateConfiguration, modelConfiguration)
    model = merge({}, state, model, { configuration })

    this.props.handleSubmit(model);
  }

  getConfiguration = () => {
    const { vendor, configuration } = this.state
    const { content_type, data, params } = configuration || {}

    delete configuration.parameters

    if (vendor === 'http_get') {
      delete configuration.content_type
      delete configuration.data
    }

    if (vendor === 'http_post') {
      configuration.data = ['application/json', 'text/plain'].includes(content_type) ? data : params
      delete configuration.params
    }

    return configuration
  }

  handleCancel = () => {
    if (this.props.onCancel) {
      this.props.onCancel();
    } else {
      history.goBack();
    }
  }

  handleIntegrationTypeChange = item => {
    if (!item) {
      return this.laodData({})
    }
    const integration_type = item && item.value || null
    const isRegistration = integration_type.includes("register") ? true : false
    let registration_consent = false

    if (isRegistration) {
      registration_consent = true
    }

    this.setState({
      registration_consent,
      integration_type,
      vendor: null,
      isRegistration,
    })
  }

  handleVendorChange = item => {
    if (!item) {
      return
    }

    const vendor = item.value
    const state = { vendor }
    if (vendor === 'trusted_form' || vendor === 'trusted_form_extend') {
      state.registration_completed = true
    }
    if (vendor === 'sca') {
      const scaConfiguration = JSON.parse(JSON.stringify(defaultSCAConfiguration))
      state.configuration = { ...this.state.configuration, ...scaConfiguration }
    }

    this.setState(state)
  }

  handleCustomChange = (field, value) => {
    const configuration = this.state.configuration || {}
    configuration[field] = value

    this.setState({ configuration })
  }

  handleChange = (field, value) => {
    this.setState({ [field]: value })
  }

  TwynePrepingToggle = () => {
    if (this.state.vendor !== 'twyne') {
      return null
    }

    return <Col size="6">
      <FormGroup>
        <Label>
          Pre-ping campaign
          <InfoTip>
            When pre-ping is toggled on Userenroll will pre-ping the Twyne campaign with all available consumer details. Only if the pre-ping succeeds will the lead post to Twyne.
          </InfoTip>
        </Label>
        <Switch id='twyne_preping' name="twyne_preping" value={this.state.twyne_preping || false} />
      </FormGroup>
    </Col>
  }

  SweepConfirmationAlert = () => {
    if (this.state.integration_type !== 'sweeps_confirmation') {
      return null
    }

    return <SimpleAlert>
      Use this URL to embed in your email: <i>https://{appStore.site.domain}.com/s-confirm?uid={'{{UserSessionID}}'}{'&'}token={'{{SweepToken}}'}</i>.
      You can use the merge tag <b>Sweep Full Confirmation URL</b> or build this url statically using the merge tags <b>User Session ID</b> and <b>Sweep Token</b>.
      The behavior of this confirmation page can be configured under the CMS.
    </SimpleAlert>
  }

  PingPost = () => {
    const { integration_type, configuration } = this.state || {}
    const { mode, allowBidSelection } = configuration || {}

    const MODE_OPTIONS = [
      { label: '1:1 Ping - Post', value: 'one_to_one_ping_post' },
      { label: 'Ping - Post', value: 'ping_post' },
      { label: 'Direct Post', value: 'only_post' },
    ]

    const MODE_FEEDBACK = {
      one_to_one_ping_post: 'A ping request is sent to fetch a single buyer, followed by a POST if the consumer completes the page with the selected buyers consent, honoring 1:1 consent.',
      ping_post: 'A ping request is sent to fetch all potential buyers, followed by a POST if the consumer completes the page associated with the buyers. A buyer will be seleted based on your Twyne selling strategy.',
      only_post: 'No ping request is sent. Only a POST is sent once the consumer completes the page and a buyer will be selected based on your Twyne selling strategy.',
    }

    const BID_FEEDBACK = {
      info: 'By enabling this option, the user will be able to individually select DBAs from the PING options.',
      alert: "You'll need to add helpers to your consent text to display DBA selection options. Only the DBAs checked by the user will be sent in the POST request to Twyne.",
    }

    const handleMode = ({ value }) => {
      if (value !== 'ping_post') {
        this.handleCustomChange('allowBidSelection', null)
      }
      this.handleCustomChange('mode', value)
    }

    const handleBidSelection = value => {
      this.handleCustomChange('allowBidSelection', value)
    }

    if (integration_type !== 'path_ping_post') {
      return null
    }

    return <>
      <Col size='6'>
        <FormGroup>
          <Label>
            Mode
            <InfoTip placement="top">{MODE_FEEDBACK[mode]}</InfoTip>
          </Label>
          <Select name="mode" value={mode} options={MODE_OPTIONS}
            handleOnChange={handleMode} clearable={false} required />
        </FormGroup>
      </Col>
      { mode === "ping_post" ? <Col size="6">
        <FormGroup>
          <Label>
            Allow selecting bids
            <InfoTip>{BID_FEEDBACK.info}</InfoTip>
          </Label>
          <Switch id="allow-bid-selection" name="allowBidSelection"
            value={allowBidSelection || false} onChange={handleBidSelection} />
        </FormGroup>
      </Col> : null}
      { mode === "ping_post" && allowBidSelection ? <Col size="12">
        <SimpleAlert variant="warning">{BID_FEEDBACK.alert}</SimpleAlert>
      </Col> : null}
    </>
  }

  DelayDropdown = () => {
    const DELAY_OPTIONS = [
      { label: 'Days', value: 'days' },
      { label: 'Hours', value: 'hours' },
    ]

    useEffect(() => {
      if (!this.state.configuration.unit) {
        this.setState({ configuration: { ...this.state.configuration, unit: 'days' } })
      }
    }, [])

    return <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button color='accent' className='flex items-center capitalize'>
          {this.state.configuration.unit}
          <ChevronDown size={12} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {DELAY_OPTIONS.map(({ label, value }) => <DropdownMenuItem
          key={value}
          onClick={() => this.setState({ configuration: { ...this.state.configuration, unit: value } })}>
          {label}
        </DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  }

  PathDelayDropdown = () => {
    const DELAY_OPTIONS = [
      { label: 'Seconds', value: 'seconds' },
      { label: 'Minutes', value: 'minutes' },
    ]

    useEffect(() => {
      if (!this.state.configuration.delay_unit) {
        this.setState({ configuration: { ...this.state.configuration, delay_unit: 'seconds' } })
      }
    }, [])

    return <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button color='accent' className='flex items-center capitalize'>
          {this.state.configuration.delay_unit || 'seconds'}
          <ChevronDown size={12} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {DELAY_OPTIONS.map(({ label, value }) => <DropdownMenuItem
          key={value}
          onClick={() => this.setState({ configuration: { ...this.state.configuration, delay_unit: value } })}>
          {label}
        </DropdownMenuItem>)}
      </DropdownMenuContent>
    </DropdownMenu>
  }

  TabDelayedRegister = ({ ...props }) => {
    if (this.state.integration_type !== 'delayed_register') {
      return null
    }

    return <TabsContent {...props} >
      <Row>
        <Col size="6">
          <FormGroup>
            <Label>Delay</Label>
            <RuleGroupItemWrapper>
              <InputGroup>
                <InputFormsy
                  type='number'
                  required
                  name="delay"
                  validationErrors={{ isRequired: '' }}
                  onChange={value => this.handleCustomChange('duration', Number(value))}
                  value={this.state.configuration.duration || ''}
                />
                <this.DelayDropdown />
              </InputGroup>
            </RuleGroupItemWrapper>
          </FormGroup>
        </Col>
        <this.TwynePrepingToggle />
      </Row>
    </TabsContent>
  }

  TabRegister = ({ ...props }) => {
    if (this.state.integration_type !== 'register') {
      return null
    }

    return <TabsContent {...props} >
      <Row>
        <this.TwynePrepingToggle />
      </Row>
    </TabsContent>
  }

  TabPathImpression = ({ ...props }) => {
    if (this.state.integration_type !== 'path_impression') {
      return null
    }

    return <TabsContent {...props} >
      <Row>
        <this.TwynePrepingToggle />
      </Row>
    </TabsContent>
  }

  TabRules = ({ render }) => {
    const { integration_type, vendor, isRegistration, rules } = this.state

    if (!isRegistration && !['revenue_share', 'path_impression', 'path_ping_post', 'reward_progression', 'lead_status_sync'].includes(integration_type) || !render) {
      return null
    }

    return <Col>
      <FormCard title='Rules'>
        <Row>
          <Col size="12">
            <FormGroup>
              <IntegrationRules
                data={rules}
                vendor={vendor}
                type={integration_type}
                onChange={rules => this.setState({ rules })}
                onConflict={this.handleConflict}
              />
            </FormGroup>
          </Col>
        </Row>
      </FormCard>
    </Col>
  }

  TabDetails = () => {
    const { vendor, integration_type } = this.state || null

    if (!vendor || integration_type === 'contest_finish') {
      return null
    }

    const VendorForm = customForms[this.state.vendor]

    return <>
      <Row>
        <Col size="12">
          <VendorForm
            vendor={this.state.vendor}
            onCustomChange={this.handleCustomChange}
            configuration={this.state.configuration}
            type={this.state.integration_type}
            integrationId={this.state.id}
            siteId={this.state.site_id}
          >
            <h4>Attributes</h4><hr />
          </VendorForm>
        </Col>
      </Row>
    </>
  }

  TabContest = () => {
    if (!this.state.vendor && this.state.integration_type !== 'contest_finish') {
      return null
    }

    return <SweepContestFinishedForm
      data={this.state.configuration}
      vendor={this.state.vendor}
      integration_type={this.state.integration_type}
      onChange={configuration => this.setState({ configuration })}
    />
  }

  PathImpressionHints = () => {
    const { dispatch } = this.state.configuration || {}

    if (!dispatch) return null

    return <>
      <InfoTip side="top" >
        {
          dispatch === 'integration-impression' ?
          'On select this trigger we dispatch your integration on the page impression.' :
          'On select this trigger we dispatch your integration only when the consumer has completed the page.'
        }
      </InfoTip>
    </>
  }

  General = () => {
    const types = useTypes()

    return <FormCard>
      <Row>
        <Col size="10">
          <FormGroup>
            <Label>Description</Label>
            <Input
              name="description"
              value={this.state.description || ''}
              required
            />
          </FormGroup>
        </Col>

        <Col size="6">
          <FormGroup>
            <Label>Integration Type</Label>
            <Select
              name="integration_type"
              handleOnChange={this.handleIntegrationTypeChange}
              options={types}
              value={this.state.integration_type}
            />
          </FormGroup>
        </Col>

        <Col size='6'>
          <FormGroup>
            <Label>{['revenue_postback', 'revenue_poller'].includes(this.state.integration_type) ? 'Origin' : 'Vendor'}</Label>
            <Select
              name="vendor"
              handleOnChange={this.handleVendorChange}
              options={useVendors(this.state)}
              value={this.state.vendor}
              required
            />
          </FormGroup>
        </Col>
        {
          this.state.integration_type === 'path_impression' && <Col size='6'>
            <FormGroup>
              <Label className='flex items-center gap-2'>
                Trigger on
                <this.PathImpressionHints />
              </Label>
              <Select
                name="dispatch"
                handleOnChange={({ value }) => this.handleCustomChange('dispatch', value)}
                options={[{ label: 'Impression', value: 'integration-impression' }, { label: 'Completed', value: 'integration-completed' }]}
                value={this.state.configuration.dispatch}
                required
              />
            </FormGroup>
          </Col>
        }
        {
          ['path_ping_post', 'path_impression'].includes(this.state.integration_type) && <Col size='6'>
            <FormGroup>
              <Label>
                Delay
                <InfoTip>
                  {this.state.integration_type === 'path_ping_post' ? 'The delay will be applied only on the post request.' : 'This delay will be applied on dispatching the integration.'}
                </InfoTip>
              </Label>
              <InputGroup>
                <Input
                  name="delay"
                  value={this.state.configuration.delay || ''}
                  onChange={({ target: { value } }) => this.handleCustomChange('delay', value)}
                  type='number'
                  required
                />
                <this.PathDelayDropdown />
              </InputGroup>
            </FormGroup>
          </Col>
        }
        <this.PingPost />
        <Col size="12">
          <this.SweepConfirmationAlert />
        </Col>
      </Row>
    </FormCard>
  }

  Tabs = () => {
    const [tabs, setTabs] = useState([])
    const { integration_type, vendor } = this.state
    const [renderRules, setRenderRules] = useState(false)

    useEffect(() => {
      const tabs = []

      if (integration_type === 'delayed_register') {
        tabs.push(['delayed_register', 'General'])
      }

      if ((integration_type === 'register' || integration_type === 'path_impression') && vendor === 'twyne') {
        tabs.push([integration_type, 'General'])
      }

      if (vendor && integration_type === 'contest_finish') {
        tabs.push(
          ['general_contest_finish', 'General'],
          ['details_contest_finish', 'Details'],
          ['configure', 'Configure'],
          ['delivery', 'Delivery'],
        )
      }

      if (vendor && integration_type !== 'contest_finish') {
        tabs.push(['details', 'Details'])
      }

      if (['delayed_register', 'register', 'revenue_share', 'path_impression', 'path_ping_post', 'reward_progression', 'lead_status_sync'].includes(integration_type)) {
        setRenderRules(true)
      }

      return setTabs(tabs)
    }, [integration_type, vendor])

    if (!tabs.length) {
      return null
    }

    return <>
      <Col>
        <FormCard title='Configuration'>
          {tabs?.length === 1 && tabs[0][0] === 'details' ? <>
            <this.TabDetails value="details" />
          </> : <>
            <Tabs defaultValue={tabs[0][0]} key={tabs[0][0]} forceMount size='sm'>
              <TabsList>
                {tabs.map(([key, label]) => <TabsTrigger className='pt-0' key={key} value={key}>{label}</TabsTrigger>)}
              </TabsList>
              <this.TabDelayedRegister value='delayed_register' />
              <this.TabRegister value="register" />
              <this.TabPathImpression value="path_impression" />
              <this.TabContest />
              <TabsContent value="details">
                <this.TabDetails value="details" />
              </TabsContent>
            </Tabs>
          </>}
        </FormCard>
      </Col>
      <this.TabRules value="rules" render={renderRules} />
    </>
  }

  render() {
    const { conflictEvent } = this.state
    const disableForm = conflictEvent === 'conflict' || conflictEvent === 'checking'
    return <Formsy onValidSubmit={this.handleValidSubmit} autoComplete={'off'}>
      <FormBody>
        <Row>
          <Col size="12">
            <this.General />
          </Col>
          <this.Tabs />
        </Row>
        <FormFooter>
          <DefaultTooltip content={disableForm && "You can't save the integration with rules conflict!"}>
            <Button type="submit" color='accent' disabled={disableForm}>{buttonLabels[conflictEvent] || 'Save'}</Button>
          </DefaultTooltip>
          <Button onClick={this.handleCancel} variant='ghost'>Cancel</Button>
        </FormFooter>
      </FormBody>
    </Formsy>
  }
}

IntegrationForm.propTypes = {
  handleSubmit: PropTypes.func,
  data: PropTypes.any,
};

export default IntegrationForm;
